/**
 * Netflix-style Profile Carousel Page
 * Displays book recommendations organized by purchase profile
 */

import { Star } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPdpUrl, isCdnMode, isPdpRedirectEnabled } from '../config/siteMode';
import {
  type BookShelf,
  getBookShelfActions,
  submitBookClick,
  submitBookShelfAction,
} from '../services/api';
import { mergeRecommendationTypes } from '../utils/recommendationTypes';
import type { TourTarget } from '../utils/tourTarget';
import { BookCard, type BookData } from './BookGrid';

const FOR_YOU_SOURCE_TAB = 'foryou';

/** Indigo.ca product-carousel chevrons (filled caret, not stroke icons). */
const CarouselChevronPrev = () => (
  <svg width="11" height="18" viewBox="0 0 11 18" fill="none" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9 2L1 10L9 18L10.4 16.6L3.8 10L10.4 3.4L9 2Z"
      fill="#2C2D2B"
    />
  </svg>
);

const CarouselChevronNext = () => (
  <svg width="11" height="18" viewBox="0 0 11 18" fill="none" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2 2L10 10L2 18L0.6 16.6L7.2 10L0.6 3.4L2 2Z"
      fill="#2C2D2B"
    />
  </svg>
);

// Mechanical carousel key emitted by the backend (RECENT_PURCHASES_CAROUSEL_NAME
// in recommendation_cache.py). It is not an LLM profile, so it has no translated
// name from the API; localize its header here instead.
const RECENT_PURCHASES_CAROUSEL_KEY = 'Based on Your Recent Purchases';

interface EnrichedProfile {
  name: string;
  description: string;
  book_count: number;
  themes?: string[];
  genres?: string[];
  for_self?: boolean;
  recipient_type?: 'self' | 'child' | 'partner' | 'gift' | 'unknown';
}

/**
 * Horizontal scroll carousel for a single profile
 */
const ProfileCarousel: React.FC<{
  profileName: string;
  /** Raw backend carousel key (stable, unlocalized) used for click attribution. */
  carouselKey: string;
  description?: string;
  themes: string[];
  books: BookData[];
  isDominant: boolean;
  showPrices?: boolean;
  hideBadges?: boolean;
  showShelfActions?: boolean;
  shopifyCustomerId?: string;
  /** Redirect the click to the storefront PDP (new tab) instead of the modal. */
  redirectToPdp?: boolean;
  /** Tour anchor tag for this row's header, when the tour points at it. */
  tourAnchor?: string;
  shelfActions: Record<string, BookShelf | null>;
  onShelfChange: (
    bookId: string,
    shelf: BookShelf | null,
    carouselKey: string,
    position: number
  ) => void;
  onBookClick: (bookId: string, carouselKey: string, position: number) => void;
}> = React.memo(
  ({
    profileName,
    carouselKey,
    description,
    themes,
    books,
    isDominant,
    showPrices = true,
    hideBadges = false,
    showShelfActions = false,
    redirectToPdp = false,
    tourAnchor,
    shelfActions,
    onShelfChange,
    onBookClick,
  }) => {
    const { t } = useTranslation();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [themesExpanded, setThemesExpanded] = useState(false);

    const updateScrollButtons = useCallback(() => {
      const el = scrollRef.current;
      if (!el) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      setCanScrollLeft(el.scrollLeft > 2);
      setCanScrollRight(maxScroll - el.scrollLeft > 2);
    }, []);

    useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      el.addEventListener('scroll', updateScrollButtons, { passive: true });
      const ro = new ResizeObserver(updateScrollButtons);
      ro.observe(el);
      return () => {
        el.removeEventListener('scroll', updateScrollButtons);
        ro.disconnect();
      };
    }, [updateScrollButtons]);

    useEffect(() => {
      if (books.length === 0) {
        setCanScrollLeft(false);
        setCanScrollRight(false);
        return;
      }
      updateScrollButtons();
    }, [books.length, updateScrollButtons]);

    const scroll = (direction: 'left' | 'right') => {
      const el = scrollRef.current;
      if (!el) return;
      // Page by nearly one row of cards so the half-peek card becomes the lead.
      const firstCard = el.firstElementChild as HTMLElement | null;
      const gap = Number.parseFloat(getComputedStyle(el).columnGap || '16') || 16;
      const step = (firstCard?.offsetWidth ?? 160) + gap;
      const pageCards = Math.max(1, Math.floor(el.clientWidth / step));
      const scrollAmount = step * pageCards;
      el.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    };

    if (books.length === 0) return null;

    return (
      <section className="pt-4 pb-3 border-b border-indigo-brand-border first:pt-0 last:border-b-0 last:pb-0">
        {/* Profile Header */}
        <div className="mb-3 px-1" data-tour-carousel-header={tourAnchor}>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-xl font-heading font-bold text-indigo-brand-text">{profileName}</h3>
            {isDominant && (
              <span
                title={t('forYou.topProfile')}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 text-xs font-semibold border border-amber-200"
              >
                <Star size={12} fill="currentColor" />
                <span className="hidden sm:inline">{t('forYou.topProfile')}</span>
              </span>
            )}
            <span className="text-xs text-indigo-brand-text/60 font-medium">
              {t('profile.bookCount', { count: books.length })}
            </span>
          </div>

          {/* Description - full text, no truncation */}
          {description && (
            <p className="text-sm text-indigo-brand-text/70 mb-1.5 max-w-3xl">{description}</p>
          )}

          {/* Theme tags — bordered chips; +N more expands the rest */}
          {themes.length > 0 && (
            <div
              className={`flex items-center gap-2 ${
                themesExpanded ? 'flex-wrap' : 'flex-nowrap overflow-hidden'
              }`}
            >
              {(themesExpanded ? themes : themes.slice(0, 3)).map((theme) => (
                <span
                  key={theme}
                  className="inline-flex items-center px-2.5 py-1 rounded-full border border-indigo-brand-border text-xs text-indigo-brand-text/80 whitespace-nowrap"
                >
                  {theme}
                </span>
              ))}
              {themes.length > 3 && (
                <button
                  type="button"
                  onClick={() => setThemesExpanded((open) => !open)}
                  className="inline-flex items-center px-2.5 py-1 rounded-full border border-indigo-brand-border text-xs text-indigo-brand-text whitespace-nowrap hover:border-indigo-brand-text/40"
                  aria-expanded={themesExpanded}
                >
                  {themesExpanded
                    ? t('forYou.fewerThemes')
                    : t('forYou.moreThemes', { count: themes.length - 3 })}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Carousel Container — Indigo.ca half-pill edge arrows */}
        <div className="relative">
          {canScrollLeft && (
            <button
              type="button"
              onClick={() => scroll('left')}
              className="hidden sm:flex absolute left-0 top-[28%] z-10 h-[50px] w-10 items-center justify-center bg-white rounded-r-full hover:bg-gray-50"
              aria-label="Previous slide"
            >
              <CarouselChevronPrev />
            </button>
          )}

          {/* Scrollable Book Row */}
          <div
            ref={scrollRef}
            data-tour-books={tourAnchor}
            className="flex gap-4 overflow-x-auto pb-2 px-1 scroll-smooth scrollbar-hide"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {books.map((book, index) => {
              const bookId = book.book_id || book.isbn || book.sku || `book-${index}`;
              return (
                // Fit N.5 cards (gap-4) so the next cover peeks as a scroll cue; lg = 6.5.
                <div
                  key={bookId}
                  className="flex-shrink-0 w-[calc((100%-2rem)/2.5)] sm:w-[calc((100%-3rem)/3.5)] md:w-[calc((100%-5rem)/5.5)] lg:w-[calc((100%-6rem)/6.5)]"
                >
                  <BookCard
                    book={book}
                    showPrices={showPrices}
                    hideBadges={hideBadges}
                    showShelfActions={showShelfActions}
                    shelf={shelfActions[bookId]}
                    onShelfChange={(shelf) => onShelfChange(bookId, shelf, carouselKey, index)}
                    // The tour spotlights the first chip of the row it explains.
                    isShelfTourAnchor={tourAnchor === 'primary' && index === 0}
                    onBookClick={() => onBookClick(bookId, carouselKey, index)}
                    redirectToPdp={redirectToPdp}
                  />
                </div>
              );
            })}
          </div>

          {canScrollRight && (
            <button
              type="button"
              onClick={() => scroll('right')}
              className="hidden sm:flex absolute right-0 top-[28%] z-10 h-[50px] w-10 items-center justify-center bg-white rounded-l-full hover:bg-gray-50"
              aria-label="Next slide"
            >
              <CarouselChevronNext />
            </button>
          )}
        </div>
      </section>
    );
  }
);

// Transform recommendations to BookData format (outside component for stable reference)
const transformToBookData = (recs: any[]): BookData[] => {
  return recs.map((rec, index) => {
    // Handle different book data formats from backend
    // Format 1: rec.book (transformed format with lowercase keys)
    // Format 2: rec.b2 (raw format with uppercase keys)
    const rawBook = rec.book || rec.b2 || {};

    // Extract with both uppercase and lowercase key support
    const book = {
      book_id: rawBook.book_id || rawBook.BOOK_ID,
      isbn: rawBook.isbn || rawBook.ISBN,
      sku: rawBook.sku || rawBook.SKU,
      title: rawBook.title || rawBook.TITLE,
      author: rawBook.author || rawBook.AUTHOR,
      price: rawBook.price || rawBook.LIST_PRICE,
      language: rawBook.language || rawBook.LANGUAGE,
      publisher: rawBook.publisher || rawBook.PUBLISHER,
      theme1: rawBook.theme1 || rawBook.THEME1,
      theme2: rawBook.theme2 || rawBook.THEME2,
      is_canadian_author: rawBook.is_canadian_author || rawBook.IS_CANADIAN_AUTHOR,
      indigo_recommendation_types: mergeRecommendationTypes(rawBook),
    };

    // Collect themes from rec.themes array or book.theme1/theme2 properties
    const recThemes = rec.themes || [];
    const bookThemes = [book.theme1, book.theme2].filter(Boolean) as string[];
    const allThemes = Array.from(new Set([...recThemes, ...bookThemes]));

    // Generate a unique ID using index as fallback
    const uniqueId = book.isbn || book.sku || book.book_id || `carousel-${index}`;

    return {
      book_id: uniqueId,
      isbn: book.isbn,
      sku: book.sku,
      title: book.title,
      author: book.author,
      price: book.price,
      language: book.language || rec.language,
      publisher: book.publisher,
      themes: allThemes,
      genres: rec.genres || [],
      recommendation_score: rec.recommendation_score,
      explanation: rec.explanation,
      sources: rec.sources,
      is_canadian_author: rec.is_canadian_author || book.is_canadian_author,
      indigo_recommendation_types: book.indigo_recommendation_types,
    };
  });
};

interface ProfileCarouselPageProps {
  results: Record<string, any>;
  /** False on the customer-facing CDN domains (prices suppressed during beta). */
  showPrices?: boolean;
  /** Hide editorial/merchandising badges (CDN mode). */
  hideBadges?: boolean;
  /** Show the shelf picker chip on each book card. */
  showShelfActions?: boolean;
  /** Shopify customer ID for attributing feedback. */
  shopifyCustomerId?: string;
  /** Row the onboarding tour explains; its header and first chip get tagged. */
  tourTarget?: TourTarget | null;
  /** Called on a deliberate interaction, to time the feedback prompt. */
  onBrowse?: () => void;
}

/**
 * Main Profile Carousel Page Component
 */
const ProfileCarouselPage: React.FC<ProfileCarouselPageProps> = ({
  results,
  showPrices = true,
  hideBadges = false,
  showShelfActions = false,
  shopifyCustomerId,
  tourTarget,
  onBrowse,
}) => {
  const { t } = useTranslation();
  const purchaseProfiles: EnrichedProfile[] = results.purchase_profiles || [];
  const dominantProfiles: EnrichedProfile[] = results.dominant_profiles || [];
  const recsByProfile: Record<string, any[]> = results.recommendations_by_profile || {};

  const dominantNames = useMemo(() => {
    return new Set(dominantProfiles.map((p) => p.name));
  }, [dominantProfiles]);

  const findProfileMetadata = useMemo(() => {
    return (carouselKey: string): EnrichedProfile | undefined =>
      purchaseProfiles.find((p) => p.name === carouselKey);
  }, [purchaseProfiles]);

  const isDominantCarousel = useMemo(() => {
    return (carouselKey: string): boolean => {
      const profile = purchaseProfiles.find((p) => p.name === carouselKey);
      return profile ? dominantNames.has(profile.name) : false;
    };
  }, [dominantNames, purchaseProfiles]);

  // Always render Netflix-style carousels from recommendations_by_profile.
  // The flat `recommendations` list remains available for APIs/analytics but is
  // not used for For You layout.
  const carouselEntries = useMemo(() => {
    return Object.entries(recsByProfile)
      .filter(([, books]) => books.length > 0)
      .map(([profileName, books]) => ({
        profileName,
        displayName:
          profileName === RECENT_PURCHASES_CAROUSEL_KEY ? t('forYou.recentPurchases') : profileName,
        books: transformToBookData(books),
        metadata: findProfileMetadata(profileName),
      }));
  }, [recsByProfile, findProfileMetadata, t]);

  const totalBooks = carouselEntries.reduce((sum, e) => sum + e.books.length, 0);

  const tourAnchorFor = useCallback(
    (carouselKey: string): string | undefined => {
      if (!tourTarget) return undefined;
      if (carouselKey === tourTarget.primaryKey) return 'primary';
      const idx = tourTarget.otherRecipients.findIndex((r) => r.key === carouselKey);
      if (idx !== -1) return `other-recipient-${idx}`;
      return undefined;
    },
    [tourTarget]
  );

  const [shelfActions, setShelfActions] = useState<Record<string, BookShelf | null>>({});

  // Restore saved picks. A pick made while this is in flight is newer than
  // the stored one, so local state wins the merge.
  useEffect(() => {
    if (!showShelfActions || !shopifyCustomerId) return;
    let cancelled = false;
    getBookShelfActions(shopifyCustomerId)
      .then((saved) => {
        if (!cancelled) setShelfActions((prev) => ({ ...saved, ...prev }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [showShelfActions, shopifyCustomerId]);

  const handleShelfChange = useCallback(
    (bookId: string, shelf: BookShelf | null, carouselKey: string, position: number) => {
      setShelfActions((prev) => ({ ...prev, [bookId]: shelf }));
      onBrowse?.();
      // Fire-and-forget: a failed write must never block the shelf UI.
      submitBookShelfAction({
        book_id: bookId,
        shelf,
        shopify_customer_id: shopifyCustomerId || '',
        carousel_name: carouselKey,
        position,
      }).catch(() => {});
    },
    [shopifyCustomerId, onBrowse]
  );

  // On CDN mode a click sends the shopper to the storefront PDP in a new tab
  // instead of opening the in-app modal; the modal stays for internal testing.
  const redirectToPdp = isPdpRedirectEnabled();

  // Click tracking is limited to customer-facing (CDN) traffic; fire-and-forget.
  const handleBookClick = useCallback(
    (bookId: string, carouselKey: string, position: number) => {
      if (!isCdnMode()) return;
      onBrowse?.();
      submitBookClick({
        book_id: bookId,
        shopify_customer_id: shopifyCustomerId || '',
        carousel_name: carouselKey,
        position,
        source_tab: FOR_YOU_SOURCE_TAB,
      }).catch(() => {});
      if (redirectToPdp) {
        window.open(getPdpUrl(bookId), '_blank', 'noopener,noreferrer');
      }
    },
    [shopifyCustomerId, redirectToPdp, onBrowse]
  );

  if (totalBooks === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-indigo-brand-border text-6xl mb-6">📚</div>
        <h2 className="text-xl font-heading text-indigo-brand-text mb-2">
          {t('forYou.emptyTitle')}
        </h2>
        <p className="text-indigo-brand-text/60 max-w-md mx-auto">{t('forYou.emptyBody')}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 px-1">
        {/* Outranks the per-carousel headings, which are text-xl. */}
        <h1 className="text-2xl font-heading font-bold text-indigo-brand-text">
          {t('forYou.title')}
        </h1>
        {/* Cold-start carousels come from stated picks, warm ones from purchases:
            claiming purchase history to a new shopper reads as broken. */}
        <p className="mt-1 text-sm text-indigo-brand-text/70 max-w-3xl">
          {results.personalization_level === 'onboarding'
            ? t('forYou.introCold')
            : t('forYou.introWarm')}
        </p>
      </div>

      {carouselEntries.map(({ profileName, displayName, books, metadata }) => (
        <ProfileCarousel
          key={profileName}
          profileName={displayName}
          carouselKey={profileName}
          description={metadata?.description}
          themes={metadata?.themes || []}
          books={books}
          isDominant={isDominantCarousel(profileName)}
          showPrices={showPrices}
          hideBadges={hideBadges}
          showShelfActions={showShelfActions}
          redirectToPdp={redirectToPdp}
          shopifyCustomerId={shopifyCustomerId}
          tourAnchor={tourAnchorFor(profileName)}
          shelfActions={shelfActions}
          onShelfChange={handleShelfChange}
          onBookClick={handleBookClick}
        />
      ))}

      <div className="mt-4 pt-3 border-t border-indigo-brand-border text-center">
        <p className="text-sm text-indigo-brand-text/50">{t('forYou.footer')}</p>
      </div>
    </div>
  );
};

export default ProfileCarouselPage;
